/**
 * keymap.ts — browser `KeyboardEvent.code` → USB HID Usage ID (Usage Page 0x07).
 *
 * SPEC §9.1 mandates a *layout-independent* wire protocol: we transmit the HID
 * usage code so the target endpoint applies its own keyboard layout (fixes the
 * AZERTY/QWERTZ "wrong char" bug). `KeyboardEvent.code` is physical-key based
 * and maps cleanly to HID usages; `KeyboardEvent.key` (layout-dependent) is only
 * used to derive the Unicode-codepoint fallback for keys we can't map.
 *
 * Reference: USB HID Usage Tables v1.12, Keyboard/Keypad Page (0x07).
 */

import { MOD } from '@/types';

/** Map of KeyboardEvent.code → HID usage id (Usage Page 0x07). */
export const CODE_TO_HID: Readonly<Record<string, number>> = {
  // Letters
  KeyA: 0x04, KeyB: 0x05, KeyC: 0x06, KeyD: 0x07, KeyE: 0x08, KeyF: 0x09,
  KeyG: 0x0a, KeyH: 0x0b, KeyI: 0x0c, KeyJ: 0x0d, KeyK: 0x0e, KeyL: 0x0f,
  KeyM: 0x10, KeyN: 0x11, KeyO: 0x12, KeyP: 0x13, KeyQ: 0x14, KeyR: 0x15,
  KeyS: 0x16, KeyT: 0x17, KeyU: 0x18, KeyV: 0x19, KeyW: 0x1a, KeyX: 0x1b,
  KeyY: 0x1c, KeyZ: 0x1d,

  // Number row
  Digit1: 0x1e, Digit2: 0x1f, Digit3: 0x20, Digit4: 0x21, Digit5: 0x22,
  Digit6: 0x23, Digit7: 0x24, Digit8: 0x25, Digit9: 0x26, Digit0: 0x27,

  // Whitespace / editing
  Enter: 0x28, Escape: 0x29, Backspace: 0x2a, Tab: 0x2b, Space: 0x2c,

  // Punctuation (physical positions)
  Minus: 0x2d, Equal: 0x2e, BracketLeft: 0x2f, BracketRight: 0x30,
  Backslash: 0x31, Semicolon: 0x33, Quote: 0x34, Backquote: 0x35,
  Comma: 0x36, Period: 0x37, Slash: 0x38, CapsLock: 0x39,

  // Function keys
  F1: 0x3a, F2: 0x3b, F3: 0x3c, F4: 0x3d, F5: 0x3e, F6: 0x3f,
  F7: 0x40, F8: 0x41, F9: 0x42, F10: 0x43, F11: 0x44, F12: 0x45,
  F13: 0x68, F14: 0x69, F15: 0x6a, F16: 0x6b, F17: 0x6c, F18: 0x6d,
  F19: 0x6e, F20: 0x6f, F21: 0x70, F22: 0x71, F23: 0x72, F24: 0x73,

  // System / navigation
  PrintScreen: 0x46, ScrollLock: 0x47, Pause: 0x48, Insert: 0x49,
  Home: 0x4a, PageUp: 0x4b, Delete: 0x4c, End: 0x4d, PageDown: 0x4e,
  ArrowRight: 0x4f, ArrowLeft: 0x50, ArrowDown: 0x51, ArrowUp: 0x52,

  // Keypad
  NumLock: 0x53, NumpadDivide: 0x54, NumpadMultiply: 0x55, NumpadSubtract: 0x56,
  NumpadAdd: 0x57, NumpadEnter: 0x58,
  Numpad1: 0x59, Numpad2: 0x5a, Numpad3: 0x5b, Numpad4: 0x5c, Numpad5: 0x5d,
  Numpad6: 0x5e, Numpad7: 0x5f, Numpad8: 0x60, Numpad9: 0x61, Numpad0: 0x62,
  NumpadDecimal: 0x63, NumpadEqual: 0x67, NumpadComma: 0x85,

  // Intl keys
  IntlBackslash: 0x64, IntlRo: 0x87, IntlYen: 0x89,
  ContextMenu: 0x65, Power: 0x66,
  Lang1: 0x90, Lang2: 0x91, Lang3: 0x92, Lang4: 0x93,
  Convert: 0x8a, NonConvert: 0x8b, KanaMode: 0x88,

  // Modifiers (left/right distinguished — HID 0xE0..0xE7)
  ControlLeft: 0xe0, ShiftLeft: 0xe1, AltLeft: 0xe2, MetaLeft: 0xe3,
  ControlRight: 0xe4, ShiftRight: 0xe5, AltRight: 0xe6, MetaRight: 0xe7,
};

/** True if the code is a modifier key (left or right variant). */
export function isModifierCode(code: string): boolean {
  const hid = CODE_TO_HID[code];
  return hid !== undefined && hid >= 0xe0 && hid <= 0xe7;
}

/** Translate a DOM KeyboardEvent to the SPEC §9.1 modifier bitmask. */
export function modsFromEvent(e: KeyboardEvent): number {
  let mods = 0;
  if (e.shiftKey) mods |= MOD.Shift;
  if (e.ctrlKey) mods |= MOD.Ctrl;
  if (e.altKey) mods |= MOD.Alt;
  if (e.metaKey) mods |= MOD.Meta;
  return mods;
}

/**
 * Resolve a KeyboardEvent into a HID usage (primary) and an optional Unicode
 * codepoint fallback. If we have no HID mapping, `usage` is 0 and the agent must
 * use `unicode`.
 */
export function resolveKey(e: KeyboardEvent): { usage: number; unicode?: number } {
  const usage = CODE_TO_HID[e.code] ?? 0;
  let unicode: number | undefined;
  // `key` is a single printable character when it represents text input.
  if (e.key.length === 1) {
    unicode = e.key.codePointAt(0);
  }
  return unicode === undefined ? { usage } : { usage, unicode };
}
